(function () {
  const LG = 990;
  const reduceMotionQuery = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );
  const hoverQuery = window.matchMedia("(hover: hover)");

  // Shopify-hosted videos and YouTube/Vimeo embeds all live in the same slot,
  // so every play/pause has to cover both the <video> and the iframe APIs.
  const setSlideMedia = (slide, action) => {
    if (!slide) return;
    const play = action === "play";

    slide.querySelectorAll("video").forEach((video) => {
      if (play) video.play().catch(() => {});
      else video.pause();
    });

    slide.querySelectorAll("iframe.js-youtube").forEach((iframe) => {
      const func = play ? "playVideo" : "pauseVideo";
      iframe.contentWindow?.postMessage(
        '{"event":"command","func":"' + func + '","args":""}',
        "*"
      );
    });

    slide.querySelectorAll("iframe.js-vimeo").forEach((iframe) => {
      const method = play ? "play" : "pause";
      iframe.contentWindow?.postMessage('{"method":"' + method + '"}', "*");
    });
  };

  // Desktop: click anywhere on a tab to swap the active media + reveal its
  // description. With autoplay on, the active tab runs a progress fill
  // (--tab-progress, written per frame) and advances to the next tab on
  // completion; the countdown holds while the section is off-screen or — with
  // Pause on hover — while the pointer rests on the media, and is skipped under
  // reduced motion. Below 990px the tab list gives way to a Swiper slider that
  // keeps activeIndex in sync, so the media of whichever slide is showing is
  // the one that plays.
  class MediaTabs {
    constructor(section) {
      this.section = section;
      this.items = Array.from(
        section.querySelectorAll("[data-media-tabs-item]")
      );
      this.triggers = this.items.map((item) =>
        item.querySelector("[data-media-tabs-trigger]")
      );
      this.descriptions = this.items.map((item) =>
        item.querySelector("[data-media-tabs-description]")
      );
      this.slides = Array.from(
        section.querySelectorAll("[data-media-tabs-slide]")
      );
      this.embeds = Array.from(
        section.querySelectorAll("iframe.js-youtube, iframe.js-vimeo")
      );
      this.sliderEl = section.querySelector(".js-media-tabs-slider");
      this.autoplay = this.sliderEl?.dataset.autoplay === "true";
      this.pauseOnHover =
        this.sliderEl?.dataset.autoplayPauseOnHover === "true";
      this.duration = Number(this.sliderEl?.dataset.autoplayDelay || 8000);

      this.activeIndex = 0;
      this.frame = null;
      this.elapsed = 0;
      this.startedAt = 0;
      this.swiper = null;
      this.resizeObserver = null;
      this.viewObserver = null;
      this.inView = true;
      this.hovered = false;
      this.focused = false;

      this.onResize = this.onResize.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.syncMedia = this.syncMedia.bind(this);
      this.tick = this.tick.bind(this);

      this.init();
    }

    get canAutoplay() {
      return (
        this.autoplay && this.slides.length > 1 && !reduceMotionQuery.matches
      );
    }

    init() {
      if (this.slides.length === 0) return;

      this.items.forEach((item, index) => {
        item.addEventListener("click", (event) =>
          this.onItemClick(event, index)
        );
      });

      // an embed that finishes loading after a state change ignored the earlier
      // postMessage, so re-apply the state once its player is there to hear it
      this.embeds.forEach((iframe) => {
        iframe.addEventListener("load", this.syncMedia);
      });

      window.addEventListener("resize", this.onResize);
      reduceMotionQuery.addEventListener("change", this.onMotionChange);

      // one hover surface for the whole section drives both the tab countdown
      // and the slider's autoplay, so Swiper's own pauseOnMouseEnter
      // stays off (it would only cover the media)
      // keyboard focus always holds both, whatever the hover setting says: the
      // next tab would make the focused description link inert
      this.section.addEventListener("focusin", this.onFocusIn);
      this.section.addEventListener("focusout", this.onFocusOut);

      if (this.pauseOnHover) {
        this.section.addEventListener("pointerenter", this.onPointerEnter);
        this.section.addEventListener("pointerleave", this.onPointerLeave);
      }

      this.setActive(0);
      this.initSlider();
      this.initViewObserver();
    }

    onItemClick(event, index) {
      // a link inside the description keeps its own action
      if (event.target.closest("a[href]")) return;
      if (index === this.activeIndex) return;

      this.setActive(index);
      this.swiper?.slideToLoop(index);
    }

    setActive(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.activeIndex = index;

      this.items.forEach((el, i) => el.classList.toggle("active", i === index));
      this.triggers.forEach((el, i) => {
        if (el)
          el.setAttribute("aria-expanded", i === index ? "true" : "false");
      });
      // the closing description stays visible for its fade-out, so `inert` is
      // what keeps a link inside it off the tab order from the click onwards
      this.descriptions.forEach((el, i) => {
        if (el) el.toggleAttribute("inert", i !== index);
      });
      this.slides.forEach((el, i) => {
        el.classList.toggle("active", i === index);
        // keep the hidden media out of the a11y tree and off the tab order
        el.toggleAttribute("inert", i !== index);
      });

      this.syncMedia();
      this.restartProgress();
    }

    syncMedia() {
      this.slides.forEach((slide, i) => {
        const shouldPlay = this.inView && i === this.activeIndex;
        setSlideMedia(slide, shouldPlay ? "play" : "pause");
      });
    }

    // the fill only animates on desktop, where the tab list is visible
    canRunProgress() {
      return this.canAutoplay && window.innerWidth >= LG;
    }

    setFill(item, progress) {
      if (!item) return;
      const clamped = Math.min(Math.max(progress, 0), 1);
      item.style.setProperty("--tab-progress", clamped * 100 + "%");
    }

    restartProgress() {
      this.stopProgress();
      this.elapsed = 0;
      this.items.forEach((el) => this.setFill(el, 0));
      this.startProgress();
    }

    startProgress() {
      this.stopProgress();

      const activeItem = this.items[this.activeIndex];
      if (!activeItem) return;

      if (!this.canRunProgress()) {
        // static full fill marks the selected tab when the countdown is off
        this.setFill(activeItem, 1);
        return;
      }

      this.setFill(activeItem, this.elapsed / this.duration);
      if (!this.inView || this.held) return;

      this.startedAt = performance.now() - this.elapsed;
      this.frame = requestAnimationFrame(this.tick);
    }

    tick(now) {
      const activeItem = this.items[this.activeIndex];
      this.elapsed = Math.min(now - this.startedAt, this.duration);
      this.setFill(activeItem, this.elapsed / this.duration);

      if (this.elapsed < this.duration) {
        this.frame = requestAnimationFrame(this.tick);
        return;
      }

      this.frame = null;
      const next = (this.activeIndex + 1) % this.slides.length;
      this.setActive(next);
    }

    stopProgress() {
      if (!this.frame) return;
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    resumeProgress() {
      if (this.frame) return;
      this.startProgress();
    }

    get held() {
      return this.hovered || this.focused;
    }

    onPointerEnter() {
      if (!hoverQuery.matches) return;
      this.hovered = true;
      this.stopProgress();
      this.swiper?.autoplay?.pause();
    }

    onPointerLeave() {
      if (!hoverQuery.matches) return;
      this.hovered = false;
      if (this.held) return;
      this.resumeProgress();
      if (this.inView) this.swiper?.autoplay?.resume();
    }

    // a mouse click focuses the tab button too; only keyboard focus holds
    onFocusIn(event) {
      if (!event.target.matches(":focus-visible")) return;
      this.focused = true;
      this.stopProgress();
      this.swiper?.autoplay?.pause();
    }

    onFocusOut(event) {
      if (!this.focused || this.section.contains(event.relatedTarget)) return;
      this.focused = false;
      if (this.held) return;
      this.resumeProgress();
      if (this.inView) this.swiper?.autoplay?.resume();
    }

    onMotionChange() {
      this.restartProgress();
      if (!this.canAutoplay) this.swiper?.autoplay?.stop();
    }

    // crossing the breakpoint hands the countdown over to (or takes it back
    // from) Swiper's own autoplay, so the run conditions are re-evaluated
    onResize() {
      this.reconcileSlider();
      this.startProgress();
    }

    initViewObserver() {
      this.viewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          this.syncMedia();

          if (this.inView) {
            // a hover outranks coming back into view, for the slider autoplay
            // just as for the countdown
            if (!this.held) this.swiper?.autoplay?.resume();
            this.resumeProgress();
          } else {
            this.swiper?.autoplay?.pause();
            this.stopProgress();
          }
        });
      });

      this.viewObserver.observe(this.section);
    }

    initSlider() {
      if (!this.sliderEl) return;
      this.reconcileSlider();
      this.resizeObserver = new ResizeObserver(() => this.reconcileSlider());
      this.resizeObserver.observe(this.section);
    }

    reconcileSlider() {
      if (!this.sliderEl || typeof Swiper === "undefined") return;
      if (window.innerWidth < LG && !this.swiper) this.buildSwiper();
      else if (window.innerWidth >= LG && this.swiper) this.destroySwiper();
    }

    buildSwiper() {
      const wrapperEl = this.sliderEl.querySelector(
        ".media-tabs__media-wrapper"
      );
      const slidesEls = this.sliderEl.querySelectorAll(
        "[data-media-tabs-slide]"
      );
      if (!wrapperEl) return;

      this.sliderEl.classList.add("swiper");
      wrapperEl.classList.add("swiper-wrapper");
      slidesEls.forEach((slide) => slide.classList.add("swiper-slide"));

      const loop = this.sliderEl.dataset.loop === "true";
      const hasPagination = this.sliderEl.dataset.pagination === "true";
      const paginationEl = this.section.querySelector(
        ".slider-pagination--progress-line"
      );

      this.swiper = new Swiper(this.sliderEl, {
        loop,
        // the track bleeds past the container, so loop mode needs a slide parked
        // beyond each edge — with only slidesPerView buffered, the peek of the
        // next slide empties out mid-swipe
        loopAdditionalSlides: 1,
        slidesPerView: 1,
        spaceBetween: 12,
        speed: 800,
        watchSlidesProgress: true,
        a11y: {
          scrollOnFocus: false,
        },
        autoplay: this.canAutoplay
          ? {
              delay: this.duration,
              disableOnInteraction: false,
            }
          : false,
        pagination:
          hasPagination && paginationEl
            ? {
                el: paginationEl,
                type: "progressbar",
              }
            : false,
        on: {
          // keep the tab state (and therefore the playing media) on the slide shown
          slideChange: (swiper) => this.setActive(swiper.realIndex),
          // Swiper re-arms its autoplay at the end of every slide transition,
          // which silently undoes a pause that landed while one was running
          autoplayResume: (swiper) => {
            if (this.held || !this.inView) swiper.autoplay.pause();
          },
        },
      });

      // Swiper only builds the loop window on the first slide change, so the
      // opening position has nothing behind the active slide and the peek that
      // advertises the backward swipe is missing until you swipe forward once
      if (loop) this.swiper.loopFix();

      // a build triggered by a breakpoint crossing starts autoplay from
      // scratch, so a hover or an off-screen section has to hold it again
      if (this.held || !this.inView) this.swiper.autoplay?.pause();
    }

    destroySwiper() {
      if (!this.swiper) return;

      const wrapperEl = this.swiper.wrapperEl;

      // destroy runs with cleanStyles off so Swiper leaves the markup alone; it
      // still restores the loop slide order and re-applies a track translate on
      // the way out, so the reset below has to come after it
      this.swiper.destroy(true, false);
      this.swiper = null;
      this.sliderEl.classList.remove("swiper");

      if (wrapperEl) {
        wrapperEl.removeAttribute("style");
        wrapperEl.classList.remove("swiper-wrapper");
      }
      this.slides.forEach((slide) => {
        slide.removeAttribute("style");
        slide.classList.remove("swiper-slide");
        slide.removeAttribute("data-swiper-slide-index");
      });
    }

    destroy() {
      this.stopProgress();
      window.removeEventListener("resize", this.onResize);
      reduceMotionQuery.removeEventListener("change", this.onMotionChange);
      this.section.removeEventListener("focusin", this.onFocusIn);
      this.section.removeEventListener("focusout", this.onFocusOut);
      this.section.removeEventListener("pointerenter", this.onPointerEnter);
      this.section.removeEventListener("pointerleave", this.onPointerLeave);
      this.embeds.forEach((iframe) => {
        iframe.removeEventListener("load", this.syncMedia);
      });

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.viewObserver) {
        this.viewObserver.disconnect();
        this.viewObserver = null;
      }

      this.slides.forEach((slide) => setSlideMedia(slide, "pause"));
      this.destroySwiper();
    }
  }

  function initSection(section) {
    if (!section || !section.classList.contains("media-tabs-section")) return;
    if (!section.querySelector(".media-tabs")) return;

    if (section._mediaTabsInstance) {
      section._mediaTabsInstance.destroy();
      section._mediaTabsInstance = null;
    }
    section._mediaTabsInstance = new MediaTabs(section);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeMediaTabsEvents) {
    window.__themeMediaTabsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (section._mediaTabsInstance) {
        section._mediaTabsInstance.destroy();
        section._mediaTabsInstance = null;
      }
    });
  }
})();
